/* ==========================================================================
   Category Products Modal
   PORTAL-77: Assign products to a category from the Categories page.
   Mirrors the CatalogAssignmentModal checklist UX; saving diffs the
   selection and updates each changed product's category_ids.
   ========================================================================== */

import { useState, useEffect, useMemo } from 'react'
import { Search } from 'lucide-react'
import { Modal } from '@/components/ui/Modal'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { listCatalog, updateProduct } from '@/api/admin'
import { ApiClientError } from '@/api/client'
import type { CatalogProductResponse, Category } from '@/api/types'

export interface CategoryProductsModalProps {
  open: boolean
  onClose: () => void
  /** The category being managed */
  category: Category | null
  /** Pharmacy portal slug */
  slug: string
  /** Called after a successful save so the parent can refresh counts */
  onSaved: (changedCount: number) => void
  /** Called with a message when loading or saving fails */
  onError: (message: string) => void
}

export function CategoryProductsModal({
  open,
  onClose,
  category,
  slug,
  onSaved,
  onError,
}: CategoryProductsModalProps) {
  const [products, setProducts] = useState<CatalogProductResponse[]>([])
  const [loadingProducts, setLoadingProducts] = useState(false)
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
  const [searchQuery, setSearchQuery] = useState('')
  const [saving, setSaving] = useState(false)
  // Bumped after a partial failure to re-fetch, so the list reflects the
  // products that did get updated before the error.
  const [reloadNonce, setReloadNonce] = useState(0)

  // Load the catalog and preselect this category's products on open
  useEffect(() => {
    if (!open || !category) return
    let cancelled = false

    async function load() {
      setLoadingProducts(true)
      setSearchQuery('')
      try {
        const response = await listCatalog(slug)
        if (cancelled) return
        setProducts(response.products)
        setSelectedIds(
          new Set(
            response.products
              .filter((p) => p.categories.some((c) => c.id === category!.id))
              .map((p) => p.id)
          )
        )
      } catch (err) {
        if (!cancelled) {
          onError(
            err instanceof ApiClientError
              ? err.message
              : 'Failed to load products. Please try again.'
          )
          onClose()
        }
      } finally {
        if (!cancelled) setLoadingProducts(false)
      }
    }

    load()
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, category?.id, slug, reloadNonce])

  const filteredProducts = useMemo(() => {
    if (!searchQuery.trim()) return products
    const query = searchQuery.toLowerCase()
    return products.filter(
      (p) =>
        p.display_name.toLowerCase().includes(query) ||
        p.product_code.toLowerCase().includes(query)
    )
  }, [products, searchQuery])

  function toggleProduct(productId: string) {
    setSelectedIds((prev) => {
      const next = new Set(prev)
      if (next.has(productId)) {
        next.delete(productId)
      } else {
        next.add(productId)
      }
      return next
    })
  }

  async function handleSave() {
    if (!category) return
    setSaving(true)

    // Each product updates independently — one failure must not abort the
    // batch or misreport the products that did change.
    let changed = 0
    const failures: string[] = []
    let firstErrorMessage: string | null = null

    try {
      for (const product of products) {
        const hasCategory = product.categories.some((c) => c.id === category.id)
        const shouldHave = selectedIds.has(product.id)
        if (hasCategory === shouldHave) continue

        const otherIds = product.categories
          .filter((c) => c.id !== category.id)
          .map((c) => c.id)
        try {
          await updateProduct(slug, product.id, {
            category_ids: shouldHave ? [...otherIds, category.id] : otherIds,
          })
          changed += 1
        } catch (err) {
          failures.push(product.product_code)
          if (!firstErrorMessage) {
            firstErrorMessage =
              err instanceof ApiClientError ? err.message : null
          }
        }
      }

      if (failures.length === 0) {
        onSaved(changed)
        onClose()
      } else {
        // Partial success: refresh parent counts for what did change, reload
        // the modal's list so it isn't stale, and report the failures.
        if (changed > 0) {
          onSaved(changed)
        }
        setReloadNonce((n) => n + 1)
        onError(
          `${changed} product${changed !== 1 ? 's' : ''} updated, but ${failures.length} failed (${failures.join(', ')})` +
            (firstErrorMessage ? `: ${firstErrorMessage}` : '. Please try again.')
        )
      }
    } finally {
      setSaving(false)
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={category ? `Products in "${category.name}"` : 'Products'}
      size="lg"
    >
      <div className="space-y-4">
        <p className="text-sm text-gray-600">
          Check the products that belong to{' '}
          <strong className="text-charcoal">{category?.name}</strong>. Products
          keep their other categories.
        </p>

        {loadingProducts ? (
          <div className="text-center py-8 text-gray-500 text-sm">
            Loading products...
          </div>
        ) : products.length === 0 ? (
          <div className="text-center py-8 text-gray-500 text-sm">
            No products in the catalog yet.
          </div>
        ) : (
          <div className="space-y-3">
            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                type="text"
                placeholder="Search products..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
              />
            </div>

            {/* Selection summary */}
            <div className="text-sm text-gray-600">
              {selectedIds.size} of {products.length} products in this category
            </div>

            {/* Product list */}
            <div className="border border-gray-200 rounded-lg max-h-72 overflow-y-auto">
              {filteredProducts.length === 0 ? (
                <div className="text-center py-6 text-gray-500 text-sm">
                  No products found matching your search.
                </div>
              ) : (
                filteredProducts.map((product) => (
                  <label
                    key={product.id}
                    className="flex items-center gap-3 px-4 py-2.5 hover:bg-gray-50 cursor-pointer border-b border-gray-100 last:border-b-0"
                  >
                    <input
                      type="checkbox"
                      checked={selectedIds.has(product.id)}
                      onChange={() => toggleProduct(product.id)}
                      disabled={saving}
                      className="w-4 h-4 rounded border-gray-300 text-deep-purple focus:ring-deep-purple"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm text-charcoal font-medium truncate">
                        {product.display_name}
                        {!product.is_active && (
                          <span className="ml-2 text-xs text-gray-400 font-normal">
                            (inactive)
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-gray-500">
                        {product.product_code}
                        {product.categories.length > 0 &&
                          ` · ${product.categories.map((c) => c.name).join(', ')}`}
                      </div>
                    </div>
                  </label>
                ))
              )}
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="flex flex-col-reverse sm:flex-row gap-3 pt-2">
          <Button
            type="button"
            variant="secondary"
            onClick={onClose}
            disabled={saving}
          >
            Cancel
          </Button>
          <Button
            type="button"
            variant="primary"
            onClick={handleSave}
            loading={saving}
            disabled={loadingProducts || products.length === 0}
            className="sm:ml-auto"
          >
            Save
          </Button>
        </div>
      </div>
    </Modal>
  )
}

export default CategoryProductsModal
